/**
 * Générateur — Diviser par un nombre décimal.
 * Attendu ID : 901 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 2)
 *
 * Cadrage : ramener une division à diviseur décimal à une division à diviseur
 * entier (1,5 ÷ 0,3 = 15 ÷ 3 = 5).
 *
 * Plan de cartes (validé par le responsable pédagogique, arbitrages inclus) :
 *
 *   Carte « transformer » — Ex.1 Transformer en fraction
 *     Formats : texte_trou_saisie, texte_trou_drag. Rendu KaTeX \dfrac avec la
 *     convention TRIPLE ACCOLADE {{{X}}} pour les trous dans un groupe KaTeX
 *     (arbitrage utilisateur). Décimales en KaTeX via {,} (pas d'espace
 *     parasite). k = max(décimales) ∈ {1,2,3} en rotation (progressivité
 *     ×10 → ×100 → ×1 000 du doc). Deux trous, toujours (cf. phraseTransformer
 *     pour l'abandon du mode « dénominateur donné »). Tout en entiers par
 *     construction. Misconceptions (drag) : décalages DIFFÉRENTS sur num. et
 *     dén., décalage d'un cran en trop.
 *
 *   Carte « quotients_egaux » — Ex.4 Quotients égaux
 *     Formats : qcm_multiple, tableau_vf, classer. 6 écritures du même couple
 *     de chiffres ; égal ⟺ le MÊME décalage est appliqué aux deux termes.
 *     Nombre de bonnes réponses VARIABLE (2 à 4), annoncé dans la consigne
 *     (« Sélectionne les trois quotients… ») — arbitrage utilisateur + §2.
 *     Vérités décidées par la construction (a, b), jamais par flottant.
 *
 *   Carte « multiplier_moins » — Ex.6 Multiplier le moins possible
 *     Formats : qcm_unique (question a), texte_trou_saisie guidé (b/c).
 *     D = m/k irréductible (gcd(m,k)=1) avec k ∈ {2, 4, 5, 10} — le cas ×10
 *     est INCLUS (arbitrage utilisateur : éviter le pattern « jamais 10 » ;
 *     diviseurs 0,3 / 0,7 / 0,9, l'exemple du cadrage). k minimal PROUVÉ par
 *     l'irréductibilité. Q ENTIER (revue 2026-08-02) → N' = m × Q entier, donc
 *     la division ramenée est « entier ÷ entier », la forme que la carte promet.
 *     Misconceptions : 10 réflexe quand k ≠ 10, multiplicateur non minimal.
 *
 *   Carte « bouteilles » — Ex.10 Le cocktail
 *     Formats : texte_trou_saisie (« 9 ÷ 0,75 = {{900}} ÷ {{75}} = {{12}}
 *     bouteilles »), qcm_unique. V = nb × c posé par construction (division
 *     exacte, nb entier). Décalage adapté aux décimales de c (×10 ou ×100).
 *     Contextes en rotation. Misconceptions : multiplier au lieu de diviser,
 *     mauvais décalage.
 *
 *   Carte « rectangles » (hors quota) — Ex.14 Les deux rectangles
 *     Formats : texte (saisie), qcm_unique. Figure SVG à l'échelle (deux
 *     rectangles, marques d'angles droits, dimensions étiquetées — reproduit
 *     l'exercice source). EF = AB × coef exact par construction (AB pair en
 *     dixièmes × coefficients demi-entiers). Les dimensions figurent AUSSI
 *     dans le texte (accessibilité + solveur).
 *     Misconceptions : EF − AB (soustrait), coef ± 0,5, EF ÷ AD.
 *
 * Adaptations d'énoncés : les batteries a–f du doc deviennent UN item par
 * tirage (Duolingo) avec rotation des difficultés ; Ex.14 : figure support,
 * zone de réponse fermée.
 *
 * ═══ Revue format par format — 2026-08-02 ═══════════════════════════════════
 *
 * Tout est chiffré sur 5 000 tirages (sonde jetable), sauf mention contraire.
 * Ce qui était SAIN et l'est resté : l'exactitude de toutes les fractions et de
 * tous les quotients produits (0 écart sur 5 000), la convention triple
 * accolade (0 rejet en rejouant buildAnswer → compareAnswers), les trois
 * encodages de « quotients égaux » (0 étiquette fausse en QCM multiple, en
 * Vrai/Faux et en Classer), la minimalité de k (0 contre-exemple en force
 * brute), la géométrie des figures (0 débordement, 0 chevauchement).
 *
 * ─── A1, le plus grave : la consigne n'imposait pas le multiplicateur ──────
 * « Transforme en fraction de nombres entiers » est satisfait par N'IMPORTE
 * QUELLE puissance de 10 assez grande : sur 2,5/3,8 l'élève qui écrivait
 * 250/380 avait mathématiquement raison et était compté faux. Mesuré : 51,7 %
 * des tirages en saisie deux trous, et 68,9 % en drag, où les DEUX
 * sur-décalages d'un même cran figuraient dans les étiquettes proposées (les
 * « misconceptions » n'en sont pas quand on les applique aux deux termes).
 * Correction : la CONSIGNE restreint le multiplicateur, « multiplie par 10, 100
 * ou 1 000, le moins possible », ce qui n'admet que 10^k. Attention au piège de
 * la reformulation : « multiplie le moins possible » tout court aurait empiré
 * les choses, le plus petit ENTIER convenable étant inférieur à 10^k dans
 * 42,8 % des tirages (2,5/3,75 → ×4 donne 10/15, entiers). C'est bien
 * l'énumération « 10, 100 ou 1 000 » qui ferme la faille, pas « le moins
 * possible » seul.
 *
 * Un premier jet affichait l'étape intermédiaire (× 10 écrit sur les deux
 * termes) : sans ambiguïté, mais cela retirait à l'élève le seul vrai savoir de
 * la carte, choisir la puissance de 10. Elle lui est rendue sous forme de trou
 * (cf. phraseTransformer, second retour utilisateur du 2026-08-02).
 *
 * ─── A2 : le rang de la bonne réponse trahissait le QCM ────────────────────
 * Une fois les quatre choix triés, la bonne réponse occupait une place fixe :
 *   - bouteilles : toujours le terme du milieu de trois entiers consécutifs,
 *     le quatrième choix étant un nombre dix fois trop grand. 100 % des
 *     tirages, sans faire une seule division.
 *   - rectangles : la plus petite des quatre dans 87,4 % (les trois erreurs
 *     modélisées étaient toutes des SUR-estimations).
 *   - multiplier le moins : la plus petite dans 75 %, jamais aux rangs 1 ni 3.
 * Correction : `distracteursEquilibres` tire uniformément le nombre d'erreurs
 * placées SOUS la bonne réponse, en puisant dans deux viviers séparés. Rangs
 * après correction : 25/26/24/24 pour les bouteilles, 24/25/26/26 pour les
 * rectangles, 31/32/19/19 pour « multiplier le moins » (k = 2 n'admet qu'un
 * seul multiplicateur plus petit, d'où le léger reste).
 *
 * ─── La double saisie du texte à trous « multiplier le moins » ─────────────
 * La ligne « (N × {{k}}) ÷ (D × {{k}}) » faisait saisir DEUX FOIS la même
 * valeur, dans 100 % des tirages. La première ligne porte maintenant le
 * raisonnement (« pour que D devienne entier, je multiplie par {{k}} ») et les
 * deux suivantes l'appliquent : quatre trous, quatre valeurs différentes.
 *
 * ─── A7 / A8 : vraisemblance et plafonds de variété ────────────────────────
 * « Chiara a préparé 36 L de cocktail » : le volume est plafonné à 20 L.
 * Espaces d'énoncés élargis là où le harnais butait, sur 5 000 tirages :
 *   - bouteilles (texte à trous)      272 →  2 939
 *   - rectangles (figure)             605 →  3 038
 *   - QCM « multiplier le moins »      30 →  4 996
 * Le QCM ne dépendait que du diviseur, soit 30 énoncés possibles en tout ; il
 * est désormais adossé à une division réelle, ce qui ouvre le dividende comme
 * axe et le remet sur l'attendu. Nouveaux axes ailleurs : 14 contenances,
 * 12 contextes de boisson, 4 jeux de sommets pour les rectangles.
 *
 * Les deux tirages à plage conditionnelle passent par une ÉNUMÉRATION des
 * couples valides (`PAIRES_BOUTEILLES`, `PAIRES_RECT`). Tirer la contenance
 * puis le nombre donnait 1 chance sur 42 à chacun des 3 couples en 2,5 L contre
 * 1 sur 770 à chacun des 55 couples en 0,2 L : l'espace effectif était bien
 * plus petit que sa taille nominale, et le harnais y trouvait des doublons.
 *
 * Harnais après correction : 10 passes sur 15 à 11 ✅ 0 ⚠️ 0 ❌, les autres avec
 * un ou deux 39/40 sur `bouteilles_texte_trou_saisie` et `rectangles_texte`.
 * C'est le régime attendu — 40 tirages dans un espace de ~3 000 collisionnent
 * avec une probabilité d'environ 12 % par format, ce n'est pas un plafond
 * structurel.
 *
 * ─── Retour de relecture : la difficulté variait sans raison ───────────────
 * La carte « transformer » alternait 1 et 2 trous. Le mode à un trou existait
 * pour fixer le multiplicateur en affichant le dénominateur ; la consigne s'en
 * charge, il ne restait qu'une difficulté inégale d'un tirage à l'autre. La
 * carte demande maintenant TROIS trous dans les deux formats : le
 * multiplicateur, puis les deux termes entiers.
 *
 * Mesures après refonte, sur 5 000 tirages de chaque format : 3 trous dans
 * 100 % des cas, jamais deux trous de même valeur, 10/100/1 000 équirépartis,
 * 0 écart de fraction, 0 rejet de scoring, 0 erreur KaTeX, et surtout 0 réponse
 * alternative cohérente plaçable en drag (recherche exhaustive : pour chaque
 * étiquette jouable m', les deux autres trous peuvent-ils recevoir num×m' et
 * den×m' ?). Le premier jet en laissait 2 sur 5 000, d'où le jeu `interdits`
 * dans genTransformerTrouDrag.
 *
 * ─── Calculatrice : NON, sur les onze formats ──────────────────────────────
 * Aucun `contenu.calculatrice` n'est posé. La technique de division EST
 * l'attendu : une calculatrice rendrait chaque format trivial, y compris les
 * rectangles (le coefficient s'obtient par une seule touche ÷).
 *
 * ─── Le QCM multi ne proposait pas la réponse que l'élève venait de calculer ─
 * Signalé en relecture (2026-08-02) : « pour les réponses, ça correspond à
 * rien ». Sur « 63,4 ÷ 9,3 », la liste offrait 63,4 ÷ 93, 6340 ÷ 9,3,
 * 63400 ÷ 930, 634 ÷ 9300, 6340 ÷ 930 et 63400 ÷ 9300. L'écriture CANONIQUE,
 * « 634 ÷ 93 » — celle que l'attendu vise, le diviseur ramené à un entier —
 * était absente. Restaient pour bonnes réponses « 6340 ÷ 930 » et
 * « 63400 ÷ 9300 », justes mais bardées de zéros inutiles que personne n'écrit.
 * Le décalage +1 étant tiré au hasard parmi quatre, il manquait environ une
 * fois sur deux. Il est désormais TOUJOURS de la partie (0 tirage sans lui sur
 * 5 000, et toujours étiqueté vrai).
 *
 * Deux fausses pistes écartées en chemin, mesurées : le SCORING est correct
 * (300 réponses parfaites, 0 comptée fausse — `resolveBonneReponse` convertit
 * bien les lettres du payload en contenus, la convention qu'émet QCMMultiEleve)
 * et l'AFFICHAGE de la réponse révélée aussi (une ligne par bonne réponse dans
 * BonneReponseEleve). Le défaut était bien dans le choix des candidats.
 *
 * Corrigé au passage : les huit couples déséquilibrés ne portaient que QUATRE
 * ratios, si bien que 55,1 % des tirages proposaient deux distracteurs de même
 * valeur (« 984 ÷ 230 » et « 9,84 ÷ 23 » sont le même dixième) et 8,7 % n'en
 * proposaient qu'une seule, en quatre exemplaires. Un ratio distinct par
 * distracteur : 0 % de doublon, 100 % de valeurs fausses distinctes. Le
 * décalage -2 est tombé au passage — il étalait la liste sur cinq décades.
 *
 * ─── Seconde passe sur « multiplier le moins », texte à trous ──────────────
 * Signalé en relecture : énoncé trop long, questions peu claires. Trois défauts
 * derrière ce constat, tous mesurés sur 5 000 tirages.
 *
 * 1. La division ramenée n'était « entier ÷ entier » que dans 0,4 % des cas
 *    (21 sur 5 000), et le dividende de départ traînait 3 décimales dans 75 %.
 *    L'élève chassait la virgule du diviseur pour tomber sur « 115,44 ÷ 13 », à
 *    poser à la main sans calculatrice — alors que la carte promet une division
 *    devenue posable, et que TOUTES les fiches d'aide montrent 54 ÷ 15,
 *    420 ÷ 35, 720 ÷ 45. Le quotient est passé en ENTIER : 100 % désormais.
 *
 * 2. L'énoncé faisait le travail du coup de pouce. 173 caractères identiques
 *    dans 5 000 tirages sur 5 000, dont deux phrases sur trois relevaient du
 *    mini-cours — la deuxième énonçait mot pour mot la fiche « Le multiplicateur
 *    s'applique aux deux nombres ». Une seule phrase de consigne reste, 73
 *    caractères. Le « trop de texte » n'était pas un excès de mots mais un cours
 *    logé dans la consigne.
 *
 * 3. Les lignes 2 et 3 partageaient le MÊME membre de gauche avec deux membres
 *    de droite différents (« N ÷ D » réécrit 2,02 fois par tirage). Lu de haut
 *    en bas, cela affichait deux résultats pour une même expression et se lisait
 *    comme deux questions séparées. Fusionné en une chaîne d'égalités, la forme
 *    du cahier : 34 caractères au pire, aucun débordement.
 *
 * Coût assumé, arbitrage utilisateur : le quotient était l'axe le plus large du
 * tirage (850 valeurs en centièmes contre ~89 en entiers). Les viviers de m sont
 * passés de 30 à 49 couples en compensation, sans suffire — l'espace effectif
 * tombe de ~20 000 à ~3 000, soit 24 % de risque de doublon sur 40 tirages.
 * `multiplier_moins_texte_trou_saisie` sort donc 39/40 environ une passe sur
 * trois, et l'énoncé du QCM descend de 4 996 à 2 523 empreintes sur 5 000. C'est
 * exactement le régime déjà assumé pour `bouteilles_texte_trou_saisie` (2 939)
 * et `rectangles_texte` (3 038). Remonter la variété imposait de regrossir les
 * nombres (plafond N' à 1 800, quotients jusqu'à 149) : on rachetait le harnais
 * en reperdant la simplicité demandée. Refusé.
 *
 * ⚠ L'élargissement des viviers rouvrait un piège : les cinq valeurs affichées
 * par les fiches d'aide (0,5 · 0,25 · 0,2 · 2,3 · 7,5) doivent rester HORS du
 * tirage, sinon une fiche sert la réponse. Vérifié après élargissement — aucune
 * n'y figure, d'où les trous délibérés à m = 15 (k=2 → 7,5) et m = 23
 * (k=10 → 2,3). Toute nouvelle valeur de m est à re-vérifier contre cette liste.
 *
 * ─── Ce qui n'a PAS été corrigé, et pourquoi ───────────────────────────────
 * L'heuristique « autant de décimales de chaque côté » résout entièrement un
 * tirage de « quotients égaux » dans 36,8 % des cas. Elle échoue sur les
 * couples (2,1) et (1,2) du vivier de déséquilibres, qui sont précisément là
 * pour ça. À deux tirages sur trois l'élève doit compter les rangs : la fuite
 * n'est pas exploitable comme stratégie.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeClasser,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 901;

const L_TRANSF = 'Transformer une écriture fractionnaire en fraction';
const L_EGAUX  = 'Reconnaître des quotients égaux';
const L_MOINS  = 'Multiplier le moins possible pour diviser';
const L_BOUT   = 'Répartir en bouteilles (division en contexte)';
const L_RECT   = 'Trouver le coefficient d\'agrandissement (figure)';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Espace insécable — colle « a ÷ b » et « par {{10}} » autour des trous (mobile). */
const NB = ' ';

/** Décale la virgule d'un nombre FR (chaînes, exact). n > 0 → ×10ⁿ. */
const decaler = (valFr, n) => {
    const [ip, dp = ''] = String(valFr).split(',');
    let digits = ip + dp;
    let pos = ip.length + n;
    while (pos > digits.length) digits += '0';
    while (pos < 0) { digits = `0${digits}`; pos++; }
    const ipNew = digits.slice(0, pos).replace(/^0+(?=\d)/, '') || '0';
    const dpNew = digits.slice(pos).replace(/0+$/, '');
    return dpNew ? `${ipNew},${dpNew}` : ipNew;
};

/** Nombre FR → nombre de décimales. */
const nbDec = (valFr) => (String(valFr).split(',')[1] || '').length;

/** Virgule française → virgule KaTeX sans espace parasite : "2,5" → "2{,}5". */
const katexFr = (valFr) => String(valFr).replace(',', '{,}');

/** Dixièmes → écriture FR. */
const dixToFr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Centièmes → écriture FR sans zéros finaux. */
const centToFr = (c) => {
    const ent = Math.floor(c / 100);
    const cents = c % 100;
    if (cents === 0) return String(ent);
    return `${ent},${String(cents).padStart(2, '0').replace(/0+$/, '')}`;
};

/** Millièmes → écriture FR sans zéros finaux. */
const milToFr = (m) => {
    const ent = Math.floor(m / 1000);
    const mil = m % 1000;
    if (mil === 0) return String(ent);
    return `${ent},${String(mil).padStart(3, '0').replace(/0+$/, '')}`;
};

/**
 * Trois distracteurs dont le nombre de valeurs situées SOUS la bonne réponse est
 * tiré uniformément. Sans cela, un pool où toutes les erreurs sont plus grandes
 * (ou plus petites) fige le rang de la bonne réponse une fois les quatre choix
 * triés, et « je prends le 2e plus petit » devient payant sans faire de maths.
 * Mesuré le 2026-08-02 avant correction : 100 % sur les bouteilles (la bonne
 * réponse était toujours le terme du milieu de trois consécutifs), 87,4 % sur
 * les rectangles, 75 % sur « multiplier le moins possible ».
 */
const distracteursEquilibres = (bonne, bas, haut) => {
    const b = [...new Set(bas)].filter(v => v !== bonne);
    const h = [...new Set(haut)].filter(v => v !== bonne);
    const lo = Math.max(0, 3 - h.length);
    const hi = Math.min(3, b.length);
    const nBas = hi >= lo ? randInt(lo, hi) : 0;
    return dedupeChoix(bonne,
        [...shuffle(b).slice(0, nBas), ...shuffle(h).slice(0, 3 - nBas)],
        [...b, ...h]);
};

/** Déduplique des choix de QCM (rotation des misconceptions par mélange). */
const dedupeChoix = (bonne, candidats, complements) => {
    const norm = (s) => String(s).replace(',', '.').trim();
    const vus = new Set([norm(bonne)]);
    const retenus = [];
    [...shuffle(candidats), ...complements].forEach(c => {
        const n = norm(c);
        if (!vus.has(n) && retenus.length < 3) { vus.add(n); retenus.push(String(c)); }
    });
    return retenus;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Transformer une écriture fractionnaire en fraction
// ═══════════════════════════════════════════════════════════

/**
 * Tire un couple (numérateur, dénominateur) décimal dont AU MOINS UN a
 * exactement k décimales (k = 1, 2 ou 3 en rotation — progressivité du doc).
 * Représentation : entier de chiffres + nombre de décimales → exactitude.
 */
const tirerDecimal = (maxDec) => {
    const dec = randInt(0, maxDec);
    let digits;
    if (dec === 0) digits = randInt(2, 99);
    else {
        // Dernier chiffre non nul → nbDec exact, pas de zéro final trompeur
        digits = randInt(Math.pow(10, dec), Math.pow(10, dec + 1) * 9 - 1);
        if (digits % 10 === 0) digits += randomChoice([1, 3, 7, 9]);
    }
    const valFr = dec === 0 ? String(digits) : decaler(String(digits), -dec);
    return { valFr, dec: nbDec(valFr) };
};

const tirerTransformer = () => {
    const k = randomChoice([1, 2, 3]);
    // Au moins un des deux termes a exactement k décimales
    let num, den;
    for (let i = 0; i < 30; i++) {
        num = tirerDecimal(k);
        den = tirerDecimal(k);
        if (Math.max(num.dec, den.dec) === k && den.valFr !== '0' && num.valFr !== den.valFr) break;
    }
    if (Math.max(num.dec, den.dec) !== k) {
        num = { valFr: decaler(String(randInt(11, 99)), -k), dec: k };
        den = { valFr: String(randInt(3, 49)), dec: 0 };
    }
    return {
        k,
        num: num.valFr,
        den: den.valFr,
        mult: String(Math.pow(10, k)),      // 10, 100 ou 1000 — demandé à l'élève
        numEntier: decaler(num.valFr, k),   // entiers par décalage de chaîne
        denEntier: decaler(den.valFr, k),
    };
};

/**
 * Phrase KaTeX en DEUX lignes, TROIS trous : le multiplicateur, puis les deux
 * termes entiers.
 *
 *   Je multiplie le numérateur et le dénominateur par $ {{100}} $.
 *   $\dfrac{0{,}8}{1{,}25} = \dfrac{{{80}}}{{{125}}}$
 *
 * Le multiplicateur est DEMANDÉ, pas affiché : choisir la bonne puissance de 10
 * est le savoir de cette carte (retour utilisateur, 2026-08-02). Il n'est
 * demandé qu'UNE FOIS — l'écrire en haut et en bas ferait saisir deux fois la
 * même valeur, le défaut corrigé sur « multiplier le moins possible ».
 *
 * ⚠ Le trou de la ligne 1 est enveloppé d'un `$ … $` inline, ce n'est pas
 * cosmétique : dès qu'UNE ligne de la phrase est du KaTeX, buildMathHtml
 * remplace TOUS les trous par du balisage LaTeX, mais ne rend en KaTeX que les
 * lignes contenant un `$`. Un trou posé sur une ligne de texte pur s'afficherait
 * tel quel, « par \rule[-0.6em]{0pt}{1.9em}\htmlClass{…} » (vérifié).
 *
 * L'AMBIGUÏTÉ A1 est levée par la CONSIGNE, pas par l'affichage : « multiplie
 * par 10, 100 ou 1 000, le moins possible » n'admet que 10^k. Formuler « le plus
 * petit nombre » rouvrirait la faille mesurée le 2026-08-02 — le plus petit
 * entier convenable est inférieur à 10^k dans 42,8 % des tirages (2,5/3,75 → ×4
 * donne 10/15, entiers).
 */
const phraseTransformer = (t) =>
    `Je multiplie le numérateur et le dénominateur par $ {{${t.mult}}} $.\n` +
    `$\\dfrac{${katexFr(t.num)}}{${katexFr(t.den)}} = \\dfrac{{{${t.numEntier}}}}{{{${t.denEntier}}}}$`;

const CONSIGNE_TRANSF = '<p>Transforme cette écriture fractionnaire en fraction de nombres entiers.</p>' +
    '<p>Multiplie par 10, 100 ou 1 000 : <strong>le moins possible</strong>.';

const genTransformerTrouSaisie = () => {
    const t = tirerTransformer();
    return makeTexteTrou(phraseTransformer(t), `${CONSIGNE_TRANSF} Complète :</p>`,
        { ...opts(L_TRANSF), mode: 'saisie' });
};

const genTransformerTrouDrag = () => {
    const t = tirerTransformer();
    // Les deux autres puissances de 10 (« pas assez » / « trop »), et les
    // sur-décalages du SEUL numérateur.
    //
    // Du seul numérateur, délibérément : en offrant aussi ceux du dénominateur,
    // l'élève pourrait poser le triplet (10^(k+1), num×10^(k+1), den×10^(k+1)),
    // cohérent et mathématiquement juste, que la consigne « le moins possible »
    // compte faux. C'est l'A1 corrigé le 2026-08-02 ; ne pas le rouvrir par la
    // porte des étiquettes.
    // Le verrou n'est pas « ne pas proposer les sur-décalages du dénominateur »
    // mais « qu'aucune étiquette ne VAILLE un sur-décalage du dénominateur » :
    // sur 5 000 tirages, 2 laissaient passer le triplet parce que den × 10^(k+1)
    // retombait sur l'étiquette « autre puissance de 10 » (cas den = 10, mult =
    // 10 : den × 100 = 1 000, qui est justement proposé).
    const attendus = new Set([t.mult, t.numEntier, t.denEntier]);
    const interdits = new Set([decaler(t.den, t.k + 1), decaler(t.den, t.k + 2)]);
    const candidats = [
        ...['10', '100', '1000'].filter((p) => p !== t.mult),
        decaler(t.num, t.k + 1), decaler(t.num, t.k + 2),
    ];
    const distracteurs = [...new Set(candidats)]
        .filter((c) => !attendus.has(c) && !interdits.has(c) && !c.includes(','));
    return makeTexteTrou(phraseTransformer(t), `${CONSIGNE_TRANSF} Glisse les bonnes étiquettes :</p>`,
        { ...opts(L_TRANSF), mode: 'drag', distracteurs });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Quotients égaux
// ═══════════════════════════════════════════════════════════

const MOTS_NOMBRE = { 2: 'deux', 3: 'trois', 4: 'quatre' };

/**
 * Couples déséquilibrés (a, b), RANGÉS PAR RATIO a − b.
 *
 * Le premier vivier alignait huit couples, mais ils ne portaient que QUATRE
 * ratios : deux distracteurs valaient alors exactement la même chose. Sur
 * 5 000 tirages (2026-08-02) : 55,1 % des QCM contenaient deux erreurs
 * identiques en valeur — « 984 ÷ 230 » et « 9,84 ÷ 23 » sont le même dixième,
 * proposé deux fois — et dans 8,7 % TOUS les distracteurs se réduisaient à une
 * seule valeur fausse. On tire donc un ratio distinct par distracteur.
 *
 * a et b restent dans [-1..3] : c'est ce qui tient les six écritures dans une
 * fourchette lisible. Avec le décalage -2 d'origine, la liste allait de
 * « 0,098 » à « 98 900 », cinq décades, et sortait des diviseurs à trois
 * décimales.
 */
const RATIOS_DESEQ = {
    '-2': [[-1, 1], [0, 2], [1, 3]],
    '-1': [[-1, 0], [0, 1], [1, 2], [2, 3]],
    '1':  [[0, -1], [1, 0], [2, 1], [3, 2]],
    '2':  [[1, -1], [2, 0], [3, 1]],
};

/**
 * Base N ÷ D (chiffres sans zéro final). Candidats = décalages (a, b) :
 * égal ⟺ a = b. Écritures toutes distinctes par construction (mêmes chiffres,
 * virgules à des positions différentes) — et depuis la revue, VALEURS fausses
 * distinctes elles aussi.
 */
const tirerQuotientsEgaux = () => {
    const nDigits = randInt(111, 989);
    const dDigits = randInt(11, 98);
    const N = decaler(String(nDigits % 10 === 0 ? nDigits + 3 : nDigits), -1);   // ex. 56,7
    const D = decaler(String(dDigits % 10 === 0 ? dDigits + 7 : dDigits), -1);   // ex. 2,8
    const nbEgaux = randInt(2, 4);
    // Le décalage +1 est TOUJOURS proposé : c'est l'écriture canonique, celle
    // que l'attendu vise (« ramener à un diviseur entier »). Sur 63,4 ÷ 9,3
    // c'est « 634 ÷ 93 ». Tiré au hasard parmi quatre, il manquait une fois sur
    // deux, et l'élève qui avait fait le bon calcul ne trouvait AUCUNE ligne
    // correspondante : restaient « 6340 ÷ 930 » et « 63400 ÷ 9300 », justes mais
    // bardées de zéros inutiles, que personne n'écrit (retour utilisateur,
    // 2026-08-02).
    const shiftsEgaux = [1, ...shuffle([-1, 2, 3]).slice(0, nbEgaux - 1)];
    // Un ratio DISTINCT par distracteur : 4 ratios pour au plus 4 distracteurs,
    // l'invariant tient toujours (nbEgaux ≥ 2 ⇒ 6 − nbEgaux ≤ 4).
    const desequilibres = shuffle(Object.keys(RATIOS_DESEQ))
        .slice(0, 6 - nbEgaux)
        .map((r) => randomChoice(RATIOS_DESEQ[r]));
    const candidats = shuffle([
        ...shiftsEgaux.map(s => ({ txt: `${decaler(N, s)} ÷ ${decaler(D, s)}`, egal: true })),
        ...desequilibres.map(([a, b]) => ({ txt: `${decaler(N, a)} ÷ ${decaler(D, b)}`, egal: false })),
    ]);
    return { N, D, nbEgaux, candidats };
};

const genQuotientsEgauxQcmMulti = () => {
    const { N, D, nbEgaux, candidats } = tirerQuotientsEgaux();
    const corrects = candidats.filter(c => c.egal).map(c => c.txt);
    const texte = `<p>Sélectionne les <strong>${MOTS_NOMBRE[nbEgaux]}</strong> quotients égaux à ` +
        `<strong>${N} ÷ ${D}</strong>.</p>`;
    return makeQcm(candidats.map(c => c.txt), corrects, texte, opts(L_EGAUX), true);
};

const genQuotientsEgauxVF = () => {
    const { N, D, candidats } = tirerQuotientsEgaux();
    // 4 lignes : on garantit au moins un vrai ET un faux par tirage
    const egaux = candidats.filter(c => c.egal).slice(0, 2);
    const differents = candidats.filter(c => !c.egal).slice(0, 2);
    const lignes4 = shuffle([...egaux, ...differents]);
    const texte = `<p>Pour chaque quotient, indique s'il est égal à <strong>${N} ÷ ${D}</strong>.</p>`;
    return makeVraiFaux(
        lignes4.map(c => `${c.txt} est égal à ${N} ÷ ${D}`),
        lignes4.map(c => (c.egal ? 0 : 1)),
        texte, opts(L_EGAUX),
    );
};

const genQuotientsEgauxClasser = () => {
    const { N, D, candidats } = tirerQuotientsEgaux();
    const categories = [
        { name: `Égal à ${N} ÷ ${D}`, items: candidats.filter(c => c.egal).map(c => c.txt) },
        { name: `Différent de ${N} ÷ ${D}`, items: candidats.filter(c => !c.egal).map(c => c.txt) },
    ];
    const texte = `<p>Classe chaque quotient : est-il égal à <strong>${N} ÷ ${D}</strong> ou non ?</p>`;
    return makeClasser(categories, texte, opts(L_EGAUX));
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Multiplier le moins possible
// ═══════════════════════════════════════════════════════════

/**
 * D = m/k irréductible → k est LE plus petit multiplicateur rendant D entier.
 * k ∈ {2, 4, 5, 10} (le cas 10 est inclus — diviseurs 0,3 / 0,7 / 0,9).
 *
 * Les diviseurs que montrent les fiches d'aide (0,5 · 0,25 · 0,2 · 2,3 · 7,5)
 * sont tenus HORS de ces viviers : une fiche ne doit jamais servir la réponse
 * d'un tirage. D'où les trous à m = 15 pour k = 2 (7,5) et m = 23 pour k = 10
 * (2,3). Contrainte de construction : gcd(m, k) = 1, faute de quoi k ne serait
 * plus le plus petit multiplicateur et l'exercice n'aurait pas de réponse.
 */
const CONFIGS_MOINS = [
    { k: 2,  ms: [3, 5, 7, 9, 11, 13, 17, 19] },                        // D = 1,5 … 9,5
    { k: 4,  ms: [3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23] },            // D = 0,75 … 5,75
    { k: 5,  ms: [2, 3, 4, 6, 7, 8, 9, 11, 12, 13,
        14, 16, 17, 18, 19, 21, 22, 23] },                              // D = 0,4 … 4,6
    { k: 10, ms: [3, 7, 9, 11, 13, 17, 19, 21, 27, 29, 31, 33] },       // D = 0,3 … 3,3
];

/**
 * Le quotient est ENTIER (revue 2026-08-02), et c'est lui qui commande tout le
 * reste : N' = m × Q est alors entier par construction, donc la division
 * ramenée est « entier ÷ entier », la forme que la carte promet.
 *
 * Avec l'ancien quotient en centièmes elle ne l'était que dans 0,4 % des
 * tirages (21 sur 5 000) : l'élève chassait la virgule du diviseur pour tomber
 * sur « 115,44 ÷ 13 », à poser à la main et sans calculatrice, alors que toutes
 * les fiches d'aide montrent 54 ÷ 15, 420 ÷ 35, 720 ÷ 45. Le dividende de
 * départ traînait 3 décimales dans 75 % des cas.
 *
 * Q est borné par 1 200 / m pour que N' reste posable : sans ce plafond,
 * m = 23 et Q = 99 donneraient 2 277 ÷ 23.
 */
const tirerMoins = () => {
    const cfg = randomChoice(CONFIGS_MOINS);
    const m = randomChoice(cfg.ms);
    const k = cfg.k;
    const q = randInt(11, Math.min(99, Math.floor(1200 / m)));
    const dFr = k === 10 ? decaler(String(m), -1)
        : k === 4 ? centToFr(m * 25)
            : k === 5 ? dixToFr(m * 2)
                : dixToFr(m * 5);                   // k=2 → m×0,5
    const nPrime = m * q;                           // N × k, entier par construction
    return {
        k, m, dFr,
        // N = N'/k, exact en centièmes : k divise 100 pour k ∈ {2, 4, 5, 10}.
        nFr: centToFr(nPrime * 100 / k),
        nPrimeFr: String(nPrime),
        qFr: String(q),
    };
};

/**
 * Multiplicateurs faux, séparés en « sous k » et « au-dessus de k ». Tous sont
 * de vraies erreurs d'élève : en dessous, on n'a pas assez multiplié (0,75 × 2
 * = 1,5, 0,4 × 4 = 1,6, et 1 = « je ne multiplie pas », le minimum naïf) ;
 * au-dessus, on a multiplié plus que nécessaire.
 */
const DISTRACTEURS_MOINS = {
    2:  { bas: ['1'],           haut: ['4', '10', '100'] },
    4:  { bas: ['1', '2', '3'], haut: ['8', '10', '100'] },
    5:  { bas: ['1', '2', '4'], haut: ['10', '100', '1000'] },
    10: { bas: ['2', '3', '5'], haut: ['20', '100', '1000'] },
};

const genMoinsQcm = () => {
    const t = tirerMoins();
    const bonne = String(t.k);
    const { bas, haut } = DISTRACTEURS_MOINS[t.k];
    const distracteurs = distracteursEquilibres(bonne, bas, haut);
    // Adossé à une DIVISION réelle, pas au seul diviseur : c'est l'attendu
    // (« diviser par un décimal ») et cela ouvre le dividende comme axe de
    // variété — l'ancienne formulation ne dépendait que de D, soit 30 énoncés
    // possibles en tout (34/40 au harnais).
    const texte = `<p>Pour calculer <strong>${t.nFr} ÷ ${t.dFr}</strong>, on multiplie les deux ` +
        `nombres par un même entier pour que le diviseur devienne entier.</p>` +
        `<p>Quel est le plus petit entier possible ?</p>`;
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_MOINS));
};

const genMoinsTrouSaisie = () => {
    const t = tirerMoins();
    // Le multiplicateur n'est demandé QU'UNE FOIS (revue 2026-08-02) : la
    // version « (N × {{k}}) ÷ (D × {{k}}) » faisait saisir deux fois la même
    // valeur dans 100 % des tirages. La ligne 1 porte le raisonnement (rendre le
    // diviseur entier), la ligne 2 l'applique et va jusqu'au bout.
    //
    // UNE SEULE chaîne d'égalités, pas deux lignes (revue 2026-08-02). La
    // version précédente réécrivait « N ÷ D » en tête des deux lignes, avec deux
    // membres de droite différents : lu de haut en bas, cela affichait deux
    // résultats pour la même expression et se lisait comme deux questions
    // séparées, alors que la seconde continue la première. La chaîne est aussi
    // la forme du cahier, et celle de la fiche d'aide (54 ÷ 15 = 3,6).
    const phrase =
        `Pour que ${t.dFr} devienne entier, je multiplie par${NB}{{${t.k}}}.\n` +
        `${t.nFr}${NB}÷${NB}${t.dFr} = {{${t.nPrimeFr}}}${NB}÷${NB}{{${t.m}}} = {{${t.qFr}}}`;
    // UNE phrase de consigne, et rien d'autre (revue 2026-08-02). La version
    // précédente en alignait trois, 173 caractères identiques dans 5 000 tirages
    // sur 5 000 : la première commentait le diviseur avant que l'élève l'ait vu
    // (il est plus bas, dans la phrase à trous), et la deuxième énonçait la
    // propriété du chapitre — mot pour mot la fiche d'aide « Le multiplicateur
    // s'applique aux deux nombres de la division ». L'énoncé servait donc le
    // rappel que l'élève est censé mobiliser, et vidait le coup de pouce de sa
    // raison d'être.
    //
    // « le moins possible » est le seul verrou A1 du format : sans lui, ×10 est
    // une réponse mathématiquement juste que le correcteur positionnel compte
    // fausse. Même verrou que sur la carte « transformer ». Ne pas l'ôter.
    //
    // « écris les nombres sans zéro inutile » est tombé : la tolérance numérique
    // est active sur ce format (saisie, non strict) et accepte déjà 5,440, 05,44
    // et 5,4400 — mesuré 0 refus sur 2 000 tirages pour chacune des trois formes.
    const texte = '<p>Rends le diviseur entier en multipliant ' +
        '<strong>le moins possible</strong>, puis calcule.</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_MOINS), mode: 'saisie' });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Répartir en bouteilles
// ═══════════════════════════════════════════════════════════

const CTX_BOUTEILLES = [
    { qui: 'Chiara', elle: true,  liquide: 'de cocktail', recipient: 'bouteilles' },
    { qui: 'Malik',  elle: false, liquide: 'de sirop de menthe', recipient: 'flacons' },
    { qui: 'Nora',   elle: true,  liquide: "d'huile d'olive", recipient: 'bidons' },
    { qui: 'Élio',   elle: false, liquide: 'de limonade', recipient: 'carafes' },
    { qui: 'Salma',  elle: true,  liquide: 'de jus de pomme', recipient: 'gourdes' },
    { qui: 'Tiago',  elle: false, liquide: 'de vinaigre', recipient: 'fioles' },
    { qui: 'Awa',    elle: true,  liquide: 'de lait ribot', recipient: 'pots' },
    { qui: 'Bastien', elle: false, liquide: 'de jus de raisin', recipient: 'pichets' },
    { qui: 'Inès',   elle: true,  liquide: 'de soupe', recipient: 'barquettes' },
    { qui: 'Youssef', elle: false, liquide: 'de thé glacé', recipient: 'thermos' },
    { qui: 'Lena',   elle: true,  liquide: 'de sirop d\'orgeat', recipient: 'doses' },
    { qui: 'Ravi',   elle: false, liquide: 'de sauce tomate', recipient: 'bocaux' },
];

// Centièmes de litre. Toutes décimales (l'attendu, c'est DIVISER par un décimal)
// et toutes plausibles en contenance de récipient.
const CONTENANCES = [20, 25, 30, 40, 50, 60, 75, 80, 120, 125, 150, 160, 175, 250];

/**
 * Tous les couples (contenance, nombre de récipients) valides, énumérés une
 * fois. Volume plafonné à 20 L : l'ancien nb ∈ [8..24] produisait « 36 L de
 * cocktail » (A7).
 *
 * On tire le COUPLE, pas la contenance puis le nombre : le plafond dépendant de
 * la contenance, l'ancien tirage en deux temps donnait 1 chance sur 42 à chacun
 * des 3 couples en 2,5 L contre 1 sur 770 à chacun des 55 couples en 0,2 L.
 * Cette non-uniformité rétrécissait l'espace effectif bien en dessous de sa
 * taille réelle et faisait ressortir des doublons au harnais.
 */
const PAIRES_BOUTEILLES = CONTENANCES.flatMap((cC) => {
    const max = Math.min(60, Math.floor(2000 / cC));
    return Array.from({ length: max - 5 }, (_, i) => [cC, i + 6]);
});

const tirerBouteilles = () => {
    const [cC, nb] = randomChoice(PAIRES_BOUTEILLES);
    const vC = nb * cC;                                // volume total en centièmes
    const j = cC % 10 === 0 ? 1 : 2;                   // décalage : ×10 ou ×100
    return {
        nb, cC, vC, ctx: randomChoice(CTX_BOUTEILLES),
        vFr: centToFr(vC), cFr: centToFr(cC),
        vDecale: decaler(centToFr(vC), j),
        cDecale: decaler(centToFr(cC), j),
    };
};

const enonceBouteilles = ({ vFr, cFr, ctx }) =>
    `<p>${ctx.qui} a préparé <strong>${vFr} L</strong> ${ctx.liquide} et veut tout répartir ` +
    `dans des ${ctx.recipient} de <strong>${cFr} L</strong>. ` +
    `De combien de ${ctx.recipient} a-t-${ctx.elle ? 'elle' : 'il'} besoin ?</p>`;

const genBouteillesTrouSaisie = () => {
    const t = tirerBouteilles();
    const phrase =
        `${t.vFr}${NB}÷${NB}${t.cFr} = {{${t.vDecale}}}${NB}÷${NB}{{${t.cDecale}}}\n` +
        `Nombre de ${t.ctx.recipient} :${NB}{{${t.nb}}}`;
    const texte = enonceBouteilles(t) + '<p>Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_BOUT), mode: 'saisie' });
};

const genBouteillesQcm = () => {
    const t = tirerBouteilles();
    const bonne = String(t.nb);
    // Ancienne liste [nb×10, nb+1, nb−1] : les trois étaient TOUJOURS retenus,
    // donc les choix formaient un nombre absurde plus trois consécutifs et la
    // bonne réponse était le terme du milieu dans 100 % des tirages (revue
    // 2026-08-02). Ici cinq candidats pour trois places : le rang varie.
    //   nb×10        → décalage appliqué au seul dividende (210 ÷ 1,5)
    //   V × c        → multiplié au lieu de divisé
    //   V ÷ (c×10^j) → décalage appliqué au seul diviseur
    //   nb ± 1, +2   → erreur de comptage
    const j = t.cC % 10 === 0 ? 1 : 2;
    const parProduit = Math.max(1, Math.round(t.vC * t.cC / 10000));
    const diviseurSeul = Math.max(1, Math.round(t.nb / Math.pow(10, j)));
    const pool = [t.nb * 10, parProduit, diviseurSeul,
        t.nb + 1, t.nb - 1, t.nb + 2, t.nb - 2, t.nb * 2].filter(v => v > 0 && v !== t.nb);
    const distracteurs = distracteursEquilibres(bonne,
        pool.filter(v => v < t.nb).map(String),
        pool.filter(v => v > t.nb).map(String));
    const texte = enonceBouteilles(t);
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_BOUT));
};

// ═══════════════════════════════════════════════════════════
// Carte 5 (hors quota) — Les deux rectangles (figure)
// ═══════════════════════════════════════════════════════════

/**
 * AB pair (dixièmes) × coefficient demi-entier → EF exact en dixièmes.
 * AD est tenu à 0,4 cm sous AB : sinon ABCD peut sortir carré, ce qui n'est pas
 * faux mais fait tiquer sur un énoncé qui dit « rectangle ». Plages élargies
 * (revue 2026-08-02) : 605 figures distinctes auparavant, d'où le 39/40.
 */
/**
 * Jeux de sommets en rotation. Quatrième axe de variété (les trois autres sont
 * AB, AD et le coefficient) : 1 118 figures distinctes sans lui. Accessoirement
 * cela empêche l'élève d'ancrer la méthode sur les lettres « AB » et « EF ».
 * Ordre : coin haut-gauche, haut-droit, bas-droit, bas-gauche.
 */
const NOMS_RECT = [
    { p: ['A', 'B', 'C', 'D'], g: ['E', 'F', 'G', 'H'] },
    { p: ['I', 'J', 'K', 'L'], g: ['M', 'N', 'P', 'Q'] },
    { p: ['R', 'S', 'T', 'U'], g: ['V', 'W', 'X', 'Y'] },
    { p: ['E', 'F', 'G', 'H'], g: ['A', 'B', 'C', 'D'] },
];

// Aucun multiple de 10 : AB est le DIVISEUR du coefficient, et un AB de 3 cm
// tout rond donnait « 7,5 ÷ 3 », une division sans décimale au dénominateur,
// donc hors attendu (A5, 20,5 % des tirages avant correction).
const AB_RECT = [22, 24, 26, 28, 32, 34, 36, 38, 42, 44, 46, 48, 52, 54, 56];

/**
 * Couples (AB, AD) valides, énumérés une fois — même raison que pour les
 * bouteilles : la plage de AD dépend de AB, donc le tirage en deux temps
 * sur-représentait les AB courts. Plancher à AB/3 : sans lui, un AB de 5,6 cm
 * pour un AD de 1 cm donne une languette illisible à côté du grand rectangle.
 */
const PAIRES_RECT = AB_RECT.flatMap((ab) => {
    const min = Math.max(10, Math.ceil(ab / 3));
    const max = Math.min(26, ab - 4);
    return Array.from({ length: max - min + 1 }, (_, i) => [ab, min + i]);
});

const tirerRectangles = () => {
    const [ab, ad] = randomChoice(PAIRES_RECT);
    const coef10 = randomChoice([15, 20, 25, 30, 35]);
    const ef = ab * coef10 / 10;                      // dixièmes, entier par parité
    return { ab, ad, coef10, ef, noms: randomChoice(NOMS_RECT) };
};

const svgRectangles = ({ ab, ad, ef, coef10, noms }) => {
    const [P1, P2, P3, P4] = noms.p;                  // petit rectangle
    const [G1, G2, G3, G4] = noms.g;                  // grand rectangle
    const s = 22;                                     // px par cm
    const w1 = ab * s / 10, h1 = ad * s / 10;
    const w2 = ef * s / 10, h2 = ad * coef10 / 10 * s / 10;
    const pad = 56, gap = 34;
    const W = Math.round(Math.max(w1, w2) + 2 * pad);
    const x1 = (W - w1) / 2, y1 = 30;
    const x2 = (W - w2) / 2, y2 = y1 + h1 + gap;
    const H = Math.round(y2 + h2 + 30);
    const angle = (x, y, dx, dy) =>
        `<path d="M ${x + dx * 9} ${y} L ${x + dx * 9} ${y + dy * 9} L ${x} ${y + dy * 9}" fill="none" stroke="#1E3A8A" stroke-width="1.2"/>`;
    const rect = (x, y, w, h) =>
        `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="none" stroke="#1E3A8A" stroke-width="2"/>` +
        angle(x, y, 1, 1) + angle(x + w, y, -1, 1) + angle(x, y + h, 1, -1) + angle(x + w, y + h, -1, -1);
    const lbl = (x, y, txt, anchor = 'middle', bold = false) =>
        `<text x="${x}" y="${y}" text-anchor="${anchor}" font-size="14"${bold ? ' font-weight="bold"' : ''} fill="#0f172a">${txt}</text>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" font-family="sans-serif">` +
        rect(x1, y1, w1, h1) + rect(x2, y2, w2, h2) +
        lbl(x1 - 8, y1 - 6, P1, 'end', true) + lbl(x1 + w1 + 8, y1 - 6, P2, 'start', true) +
        lbl(x1 - 8, y1 + h1 + 14, P4, 'end', true) + lbl(x1 + w1 + 8, y1 + h1 + 14, P3, 'start', true) +
        lbl(x1 + w1 / 2, y1 - 8, `${dixToFr(ab)} cm`) +
        lbl(x1 - 12, y1 + h1 / 2 + 5, `${dixToFr(ad)} cm`, 'end') +
        lbl(x2 - 8, y2 - 6, G1, 'end', true) + lbl(x2 + w2 + 8, y2 - 6, G2, 'start', true) +
        lbl(x2 - 8, y2 + h2 + 14, G4, 'end', true) + lbl(x2 + w2 + 8, y2 + h2 + 14, G3, 'start', true) +
        lbl(x2 + w2 / 2, y2 - 8, `${dixToFr(ef)} cm`) +
        '</svg>';
};

const imageElement = (svg) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' },
    }],
});

const enonceRectangles = ({ ab, ad, ef, noms }) => {
    const petit = noms.p.join('');
    const grand = noms.g.join('');
    return `<p>Les dimensions du rectangle ${petit} (${noms.p[0]}${noms.p[1]} = ${dixToFr(ab)} cm, ` +
        `${noms.p[0]}${noms.p[3]} = ${dixToFr(ad)} cm) ont été multipliées par un même nombre pour ` +
        `obtenir celles du rectangle ${grand} (${noms.g[0]}${noms.g[1]} = ${dixToFr(ef)} cm).</p>`;
};

const genRectanglesSaisie = () => {
    const t = tirerRectangles();
    const texte = enonceRectangles(t) +
        '<p>Calcule ce nombre. Écris ta réponse en écriture décimale (ex. : 2,5).</p>';
    return makeSaisie(dixToFr(t.coef10), texte, {
        ...opts(L_RECT),
        elements_additionnels: imageElement(svgRectangles(t)),
    });
};

const genRectanglesQcm = () => {
    const t = tirerRectangles();
    const bonne = dixToFr(t.coef10);
    // Les trois distracteurs d'origine (EF − AB, coef + 0,5, EF ÷ AD) étaient
    // TOUS supérieurs au coefficient : la bonne réponse était la plus petite des
    // quatre dans 87,4 % des tirages (revue 2026-08-02). On ajoute au pool deux
    // erreurs qui tombent EN DESSOUS — AB ÷ EF (division à l'envers) et
    // coef − 0,5 — et on tire trois places parmi cinq.
    const efSurAd = Math.round(t.ef * 10 / t.ad);     // EF ÷ AD, mauvaise dimension
    const abSurEf = Math.max(1, Math.round(t.ab * 10 / t.ef));  // division à l'envers
    const bas = [abSurEf, t.coef10 - 5, t.coef10 - 10, t.coef10 - 15]
        .filter(v => v > 0 && v < t.coef10).map(dixToFr);
    const haut = [t.ef - t.ab, t.coef10 + 5, t.coef10 + 10, efSurAd]
        .filter(v => v > t.coef10).map(dixToFr);
    const distracteurs = distracteursEquilibres(bonne, bas, haut);
    const texte = enonceRectangles(t) + '<p>Calcule ce nombre.</p>';
    const payload = makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_RECT));
    payload.elements_additionnels = imageElement(svgRectangles(t));
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    transformer_texte_trou_saisie:     genTransformerTrouSaisie,
    transformer_texte_trou_drag:       genTransformerTrouDrag,
    quotients_egaux_qcm_multiple:      genQuotientsEgauxQcmMulti,
    quotients_egaux_tableau_vf:        genQuotientsEgauxVF,
    quotients_egaux_classer:           genQuotientsEgauxClasser,
    multiplier_moins_qcm_unique:       genMoinsQcm,
    multiplier_moins_texte_trou_saisie: genMoinsTrouSaisie,
    bouteilles_texte_trou_saisie:      genBouteillesTrouSaisie,
    bouteilles_qcm_unique:             genBouteillesQcm,
    rectangles_texte:                  genRectanglesSaisie,
    rectangles_qcm_unique:             genRectanglesQcm,
};

export const CATEGORIES = [
    {
        id: 'transformer', label: L_TRANSF,
        formatsList: [
            { key: 'transformer_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'transformer_texte_trou_drag',   label: 'Drag' },
        ],
    },
    {
        id: 'quotients_egaux', label: L_EGAUX,
        formatsList: [
            { key: 'quotients_egaux_qcm_multiple', label: 'QCM Multi' },
            { key: 'quotients_egaux_tableau_vf',   label: 'Vrai/Faux' },
            { key: 'quotients_egaux_classer',      label: 'Classer' },
        ],
    },
    {
        id: 'multiplier_moins', label: L_MOINS,
        formatsList: [
            { key: 'multiplier_moins_qcm_unique',        label: 'QCM' },
            { key: 'multiplier_moins_texte_trou_saisie', label: 'Saisie trous' },
        ],
    },
    {
        id: 'bouteilles', label: L_BOUT,
        formatsList: [
            { key: 'bouteilles_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'bouteilles_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'rectangles', label: L_RECT,
        formatsList: [
            { key: 'rectangles_texte',      label: 'Saisie' },
            { key: 'rectangles_qcm_unique', label: 'QCM' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    decaler, nbDec, katexFr, dixToFr, centToFr, milToFr,
    tirerTransformer, tirerQuotientsEgaux, tirerMoins, tirerBouteilles, tirerRectangles,
    CONFIGS_MOINS, svgRectangles, PAIRES_BOUTEILLES, PAIRES_RECT,
};
